// src/lume-client.ts
import {
  iceCandidateMessageSchema,
  sdpMessageSchema,
  SIGNALING_EVENTS
} from "@lume/protocol";
import { io } from "socket.io-client";

// src/adaptive-bitrate.ts
var DEFAULT_ADAPTIVE_BITRATE = {
  minBps: 3e5,
  maxBps: 8e6,
  initialBps: 25e5,
  sampleIntervalMs: 2e3,
  hysteresisMs: 4e3,
  scaleUpFactor: 1.15,
  scaleDownFactor: 0.8,
  lossHigh: 0.05,
  lossLow: 0.01,
  rttHighMs: 300,
  rttLowMs: 150,
  goodSamplesForUpscale: 3
};
var AdaptiveBitrateController = class {
  config;
  onChange;
  now;
  sender = null;
  peer = null;
  timer = null;
  currentBps;
  lastChangeAt = 0;
  goodSamples = 0;
  lastPacketsSent = 0;
  lastPacketsLost = 0;
  constructor(config, onChange, now = Date.now) {
    this.config = { ...DEFAULT_ADAPTIVE_BITRATE, ...config ?? {} };
    this.onChange = onChange;
    this.now = now;
    this.currentBps = this.clamp(this.config.initialBps);
  }
  /**
   * Wire the controller to a peer connection and its video sender, then
   * apply the initial bitrate and start the sampling loop.
   */
  start(peer, sender) {
    this.stop();
    this.peer = peer;
    this.sender = sender;
    void this.applyBitrate(this.currentBps);
    this.onChange({
      bps: this.currentBps,
      reason: "initial",
      lossFraction: null,
      rttMs: null
    });
    this.timer = setInterval(() => {
      void this.tick();
    }, this.config.sampleIntervalMs);
  }
  /** Stop the loop. Idempotent. The sender is left at its current bitrate. */
  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.peer = null;
    this.sender = null;
  }
  /** Current target bitrate in bps. */
  get bitrate() {
    return this.currentBps;
  }
  async tick() {
    if (!this.peer || !this.sender) {
      return;
    }
    let lossFraction = null;
    let rttMs = null;
    try {
      const report = await this.peer.getStats(this.sender.track ?? void 0);
      const sample = this.extractSample(report);
      lossFraction = sample.lossFraction;
      rttMs = sample.rttMs;
    } catch {
      return;
    }
    const timeSinceChange = this.now() - this.lastChangeAt;
    if (timeSinceChange < this.config.hysteresisMs) {
      return;
    }
    const lossBad = lossFraction !== null && lossFraction >= this.config.lossHigh;
    const rttBad = rttMs !== null && rttMs >= this.config.rttHighMs;
    if (lossBad || rttBad) {
      const next = this.clamp(this.currentBps * this.config.scaleDownFactor);
      if (next !== this.currentBps) {
        this.goodSamples = 0;
        void this.commitChange(next, "scaled-down", lossFraction, rttMs);
      }
      return;
    }
    const lossGood = lossFraction === null || lossFraction < this.config.lossLow;
    const rttGood = rttMs === null || rttMs < this.config.rttLowMs;
    if (lossGood && rttGood) {
      this.goodSamples += 1;
      if (this.goodSamples >= this.config.goodSamplesForUpscale) {
        const next = this.clamp(this.currentBps * this.config.scaleUpFactor);
        if (next !== this.currentBps) {
          this.goodSamples = 0;
          void this.commitChange(next, "scaled-up", lossFraction, rttMs);
        }
      }
      return;
    }
    this.goodSamples = 0;
  }
  extractSample(report) {
    let lossFraction = null;
    let rttMs = null;
    let outbound = null;
    let remoteInbound = null;
    let selectedPair = null;
    report.forEach((value) => {
      if (value.type === "outbound-rtp" && value.kind === "video") {
        outbound = value;
      } else if (value.type === "remote-inbound-rtp" && value.kind === "video") {
        remoteInbound = value;
      } else if (value.type === "candidate-pair" && value.state === "succeeded" && (value.nominated === true || value.selected === true)) {
        selectedPair = value;
      }
    });
    if (remoteInbound) {
      const rib = remoteInbound;
      if (typeof rib.fractionLost === "number") {
        lossFraction = rib.fractionLost > 1 ? rib.fractionLost / 255 : rib.fractionLost;
      } else if (outbound) {
        const ob = outbound;
        const sent = ob.packetsSent ?? 0;
        const lost = rib.packetsLost ?? 0;
        const deltaSent = sent - this.lastPacketsSent;
        const deltaLost = lost - this.lastPacketsLost;
        if (deltaSent > 0) {
          lossFraction = Math.max(0, deltaLost) / (deltaSent + Math.max(0, deltaLost));
        }
        this.lastPacketsSent = sent;
        this.lastPacketsLost = lost;
      }
    }
    if (selectedPair) {
      const cp = selectedPair;
      if (typeof cp.currentRoundTripTime === "number") {
        rttMs = cp.currentRoundTripTime * 1e3;
      }
    }
    return { lossFraction, rttMs };
  }
  async commitChange(next, reason, lossFraction, rttMs) {
    const applied = await this.applyBitrate(next);
    if (!applied) {
      return;
    }
    this.currentBps = next;
    this.lastChangeAt = this.now();
    this.onChange({ bps: next, reason, lossFraction, rttMs });
  }
  async applyBitrate(bps) {
    if (!this.sender) {
      return false;
    }
    const params = this.sender.getParameters();
    const encodings = params.encodings && params.encodings.length > 0 ? params.encodings.map((enc) => ({ ...enc, maxBitrate: bps })) : [{ maxBitrate: bps }];
    params.encodings = encodings;
    try {
      await this.sender.setParameters(params);
      return true;
    } catch {
      return false;
    }
  }
  clamp(bps) {
    return Math.max(this.config.minBps, Math.min(this.config.maxBps, Math.round(bps)));
  }
};

// src/codec-preference.ts
var DEFAULT_VIDEO_CODEC_PREFERENCE = [
  "VP9",
  "VP8",
  "H264"
];
var MIME_BY_CODEC = {
  AV1: "video/AV1",
  VP9: "video/VP9",
  VP8: "video/VP8",
  H264: "video/H264"
};
function applyVideoCodecPreference(transceiver, preference = DEFAULT_VIDEO_CODEC_PREFERENCE) {
  if (typeof transceiver.setCodecPreferences !== "function") {
    return null;
  }
  const senderCaps = RTCRtpSender.getCapabilities?.("video") ?? null;
  const receiverCaps = RTCRtpReceiver.getCapabilities?.("video") ?? null;
  const available = senderCaps?.codecs ?? receiverCaps?.codecs ?? null;
  if (!available || available.length === 0) {
    return null;
  }
  const preferredMimes = preference.map((codec) => MIME_BY_CODEC[codec].toLowerCase());
  const matches = /* @__PURE__ */ new Map();
  const rest = [];
  for (const codec of available) {
    const idx = preferredMimes.indexOf(codec.mimeType.toLowerCase());
    if (idx === -1) {
      rest.push(codec);
      continue;
    }
    const bucket = matches.get(idx) ?? [];
    bucket.push(codec);
    matches.set(idx, bucket);
  }
  const ordered = [];
  for (let i = 0; i < preference.length; i++) {
    const bucket = matches.get(i);
    if (bucket) {
      ordered.push(...bucket);
    }
  }
  ordered.push(...rest);
  try {
    transceiver.setCodecPreferences(ordered);
    return ordered;
  } catch {
    return null;
  }
}

// src/events.ts
var TypedEmitter = class {
  handlers = /* @__PURE__ */ new Map();
  on(event, handler) {
    let bucket = this.handlers.get(event);
    if (!bucket) {
      bucket = /* @__PURE__ */ new Set();
      this.handlers.set(event, bucket);
    }
    bucket.add(handler);
    return () => this.off(event, handler);
  }
  off(event, handler) {
    this.handlers.get(event)?.delete(handler);
  }
  emit(event, payload) {
    const bucket = this.handlers.get(event);
    if (!bucket) {
      return;
    }
    for (const handler of [...bucket]) {
      try {
        handler(payload);
      } catch (err) {
        console.error("[lume-webrtc] listener threw", err);
      }
    }
  }
  /** Drop every listener. Useful in cleanup paths. */
  clearAllListeners() {
    this.handlers.clear();
  }
};

// src/lume-client.ts
var LumeClient = class extends TypedEmitter {
  options;
  socket = null;
  peer = null;
  hostSocketId = null;
  mySocketId = null;
  state = "idle";
  stopped = false;
  bitrateController = null;
  constructor(options) {
    super();
    this.options = options;
    if (options.adaptiveBitrate !== false) {
      this.bitrateController = new AdaptiveBitrateController(
        options.adaptiveBitrate ?? void 0,
        (payload) => this.emit("bitrateChange", payload)
      );
    }
  }
  /** Current high-level state. Mirrors the last `stateChange` event. */
  get currentState() {
    return this.state;
  }
  /**
   * The live RTCPeerConnection, or null before `connect()` completes and
   * after `disconnect()`. Exposed so consumers can call `getStats()` for
   * telemetry. Do not mutate the connection from outside.
   */
  get peerConnection() {
    return this.peer;
  }
  /**
   * Open the signaling connection and arm the WebRTC pipeline. Resolves
   * once the signaling socket is live; the WebRTC negotiation continues
   * asynchronously and is reported via `stateChange` events.
   */
  async connect() {
    if (this.stopped) {
      throw new Error("LumeClient has been disconnected and cannot reconnect");
    }
    if (this.socket) {
      return;
    }
    this.transition("signaling-connecting");
    this.peer = new RTCPeerConnection({ iceServers: this.options.iceServers });
    let videoSender = null;
    for (const track of this.options.stream.getTracks()) {
      const sender = this.peer.addTrack(track, this.options.stream);
      if (track.kind === "video") {
        videoSender = sender;
      }
      track.addEventListener("ended", () => this.disconnect("media-revoked"));
    }
    if (this.options.preferredCodecs !== null) {
      const preference = this.options.preferredCodecs ?? DEFAULT_VIDEO_CODEC_PREFERENCE;
      const videoTransceiver = this.peer.getTransceivers().find((t) => t.sender.track?.kind === "video");
      if (videoTransceiver) {
        applyVideoCodecPreference(videoTransceiver, preference);
      }
    }
    if (this.bitrateController && videoSender) {
      this.bitrateController.start(this.peer, videoSender);
    } else {
      this.applyFallbackBitrate();
    }
    this.peer.oniceconnectionstatechange = () => this.onIceState();
    this.peer.onicecandidate = (event) => this.forwardIceCandidate(event.candidate);
    this.socket = io(this.options.signalingUrl, {
      transports: ["websocket", "polling"],
      auth: {
        role: "client",
        token: this.options.joinToken,
        sessionCode: this.options.sessionCode
      },
      reconnectionAttempts: this.options.reconnectionAttempts ?? 5,
      reconnectionDelay: 800
    });
    this.socket.on("connect", () => {
      this.mySocketId = this.socket?.id ?? null;
      this.transition("awaiting-peer");
      this.emit("signalingReady", void 0);
    });
    this.socket.on("connect_error", (err) => {
      this.emit("error", { code: "NETWORK", message: err.message, cause: err });
      if (isFatalSignalingCode(err.name)) {
        this.disconnect("signaling-error", err.message);
        return;
      }
      this.transition("reconnecting");
    });
    this.socket.on("disconnect", (reason) => {
      if (this.stopped || reason === "io client disconnect") {
        return;
      }
      if (reason === "io server disconnect") {
        this.disconnect("signaling-error", "the server closed the connection");
        return;
      }
      this.transition("reconnecting");
    });
    this.socket.on(SIGNALING_EVENTS.error, (event) => {
      this.emit("error", { code: event.code, message: event.message });
      if (isFatalSignalingCode(event.code)) {
        this.disconnect("signaling-error", event.message);
      }
    });
    this.socket.on(SIGNALING_EVENTS.peerJoined, (event) => {
      if (event.role !== "host") {
        return;
      }
      this.hostSocketId = event.socketId;
      void this.negotiate();
    });
    this.socket.on(SIGNALING_EVENTS.peerLeft, (event) => {
      if (event.role === "host") {
        this.disconnect("host-left");
      }
    });
    this.socket.on(SIGNALING_EVENTS.sdp, (raw) => this.onSdpMessage(raw));
    this.socket.on(SIGNALING_EVENTS.ice, (raw) => this.onIceMessage(raw));
  }
  /** Tear down the peer connection and signaling socket. Idempotent. */
  disconnect(reason = "user", message) {
    if (this.stopped) {
      return;
    }
    this.stopped = true;
    this.bitrateController?.stop();
    for (const track of this.options.stream.getTracks()) {
      track.stop();
    }
    try {
      this.peer?.close();
    } catch {
    }
    this.peer = null;
    this.socket?.disconnect();
    this.socket = null;
    this.transition("closed");
    this.emit("disconnect", { reason, message });
    this.clearAllListeners();
  }
  /* ------------------------------ Internals ----------------------------- */
  transition(next) {
    if (this.state === next) {
      return;
    }
    this.state = next;
    this.emit("stateChange", { state: next });
  }
  /**
   * Static cap used only when adaptive bitrate is disabled by the caller.
   * The default ceiling matches the upper bound of the adaptive range so
   * that opting out does not silently reduce headroom.
   */
  applyFallbackBitrate() {
    if (!this.peer) {
      return;
    }
    const cfg = this.options.adaptiveBitrate;
    const cap = cfg ? cfg.maxBps ?? 4e6 : 4e6;
    for (const sender of this.peer.getSenders()) {
      const params = sender.getParameters();
      params.encodings = params.encodings?.length ? params.encodings.map((encoding) => ({ ...encoding, maxBitrate: cap })) : [{ maxBitrate: cap }];
      void sender.setParameters(params).catch(() => {
      });
    }
  }
  async negotiate() {
    if (!this.peer || !this.socket || !this.hostSocketId || !this.mySocketId) {
      return;
    }
    this.transition("negotiating");
    try {
      const offer = await this.peer.createOffer();
      await this.peer.setLocalDescription(offer);
      const message = {
        to: this.hostSocketId,
        from: this.mySocketId,
        description: { type: offer.type, sdp: offer.sdp ?? "" }
      };
      this.socket.emit(SIGNALING_EVENTS.sdp, message);
    } catch (err) {
      this.emit("error", { code: "UNEXPECTED", message: "offer/setLocalDescription failed", cause: err });
      this.disconnect("ice-failed");
    }
  }
  onIceState() {
    const state = this.peer?.iceConnectionState;
    switch (state) {
      case "checking":
        this.transition("negotiating");
        break;
      case "connected":
      case "completed":
        this.transition("connected");
        break;
      case "disconnected":
        this.transition("reconnecting");
        break;
      case "failed":
        try {
          this.peer?.restartIce();
          this.transition("reconnecting");
        } catch {
          this.disconnect("ice-failed");
        }
        break;
      case "closed":
        if (!this.stopped) {
          this.disconnect("ice-failed");
        }
        break;
      default:
        break;
    }
  }
  forwardIceCandidate(candidate) {
    if (!this.socket || !this.hostSocketId || !this.mySocketId) {
      return;
    }
    const message = {
      to: this.hostSocketId,
      from: this.mySocketId,
      candidate: candidate ? candidate.toJSON() : null
    };
    this.socket.emit(SIGNALING_EVENTS.ice, message);
  }
  async onSdpMessage(raw) {
    const parsed = sdpMessageSchema.safeParse(raw);
    if (!parsed.success || !this.peer) {
      return;
    }
    if (!this.isFromHost(parsed.data.from, "SDP")) {
      return;
    }
    if (parsed.data.description.type !== "answer") {
      return;
    }
    try {
      await this.peer.setRemoteDescription(parsed.data.description);
    } catch (err) {
      this.emit("error", { code: "UNEXPECTED", message: "setRemoteDescription failed", cause: err });
    }
  }
  async onIceMessage(raw) {
    const parsed = iceCandidateMessageSchema.safeParse(raw);
    if (!parsed.success || !parsed.data.candidate || !this.peer) {
      return;
    }
    if (!this.isFromHost(parsed.data.from, "ICE")) {
      return;
    }
    try {
      await this.peer.addIceCandidate(parsed.data.candidate);
    } catch (err) {
      this.emit("error", { code: "UNEXPECTED", message: "addIceCandidate failed", cause: err });
    }
  }
  /**
   * Accept negotiation traffic only from the technician we are paired with.
   *
   * The server stamps `from` with the real socket id, so it cannot be forged;
   * what was missing was checking it at all. Without this, any other peer in
   * the room could send an SDP answer and take over the media path, pointing
   * the customer's screen share at themselves.
   */
  isFromHost(from, kind) {
    if (this.hostSocketId && from === this.hostSocketId) {
      return true;
    }
    this.emit("error", {
      code: "UNEXPECTED",
      message: `ignored ${kind} from an unexpected peer`
    });
    return false;
  }
};
function isFatalSignalingCode(code) {
  return code === "INVALID_TOKEN" || code === "INVALID_SESSION" || code === "SESSION_FULL";
}

// src/lume-host.ts
import {
  iceCandidateMessageSchema as iceCandidateMessageSchema2,
  sdpMessageSchema as sdpMessageSchema2,
  SIGNALING_EVENTS as SIGNALING_EVENTS2
} from "@lume/protocol";
import { io as io2 } from "socket.io-client";
var LumeHost = class extends TypedEmitter {
  options;
  socket = null;
  mySocketId = null;
  peers = /* @__PURE__ */ new Map();
  state = "idle";
  stopped = false;
  constructor(options) {
    super();
    this.options = options;
  }
  get currentState() {
    return this.state;
  }
  /** Returns the active remote streams keyed by client socket id. */
  get remoteStreams() {
    const out = /* @__PURE__ */ new Map();
    for (const [id, peer] of this.peers) {
      out.set(id, peer.remoteStream);
    }
    return out;
  }
  /**
   * Live RTCPeerConnections, keyed by client socket id. Exposed so the host
   * UI can call `getStats()` for telemetry (latency, fps, bitrate). The map
   * is a snapshot. Do not mutate the connections from outside.
   */
  get peerConnections() {
    const out = /* @__PURE__ */ new Map();
    for (const [id, peer] of this.peers) {
      out.set(id, peer.pc);
    }
    return out;
  }
  async connect() {
    if (this.stopped) {
      throw new Error("LumeHost has been disconnected and cannot reconnect");
    }
    if (this.socket) {
      return;
    }
    this.transition("signaling-connecting");
    this.socket = io2(this.options.signalingUrl, {
      transports: ["websocket", "polling"],
      auth: {
        role: "host",
        token: this.options.hostToken,
        sessionCode: this.options.sessionCode
      },
      reconnectionAttempts: this.options.reconnectionAttempts ?? 5,
      reconnectionDelay: 800
    });
    this.socket.on("connect", () => {
      this.mySocketId = this.socket?.id ?? null;
      this.transition("awaiting-peer");
      this.emit("signalingReady", void 0);
    });
    this.socket.on("connect_error", (err) => {
      this.emit("error", { code: "NETWORK", message: err.message, cause: err });
      this.disconnect("signaling-error", err.message);
    });
    this.socket.on(SIGNALING_EVENTS2.error, (event) => {
      this.emit("error", { code: event.code, message: event.message });
      this.disconnect("signaling-error", event.message);
    });
    this.socket.on(SIGNALING_EVENTS2.peerJoined, (event) => {
      if (event.role !== "client") {
        return;
      }
      this.handlePeerJoined(event.socketId);
    });
    this.socket.on(SIGNALING_EVENTS2.peerLeft, (event) => {
      if (event.role === "client") {
        this.handlePeerLeft(event.socketId);
      }
    });
    this.socket.on(SIGNALING_EVENTS2.sdp, (raw) => this.onSdpMessage(raw));
    this.socket.on(SIGNALING_EVENTS2.ice, (raw) => this.onIceMessage(raw));
  }
  /** Close every peer connection and disconnect from signaling. Idempotent. */
  disconnect(reason = "user", message) {
    if (this.stopped) {
      return;
    }
    this.stopped = true;
    for (const peer of this.peers.values()) {
      try {
        peer.pc.close();
      } catch {
      }
    }
    this.peers.clear();
    this.socket?.disconnect();
    this.socket = null;
    this.transition("closed");
    this.emit("disconnect", { reason, message });
    this.clearAllListeners();
  }
  /* ------------------------------ Internals ----------------------------- */
  transition(next) {
    if (this.state === next) {
      return;
    }
    this.state = next;
    this.emit("stateChange", { state: next });
  }
  handlePeerJoined(socketId) {
    if (this.peers.has(socketId)) {
      return;
    }
    const pc = new RTCPeerConnection({ iceServers: this.options.iceServers });
    const remoteStream = new MediaStream();
    const peer = { socketId, pc, remoteStream };
    pc.ontrack = (event) => {
      remoteStream.addTrack(event.track);
      this.emit("stream", { socketId, stream: remoteStream });
      this.transition("connected");
    };
    pc.onicecandidate = (event) => {
      this.forwardIceCandidate(socketId, event.candidate);
    };
    pc.oniceconnectionstatechange = () => {
      switch (pc.iceConnectionState) {
        case "checking":
          this.transition("negotiating");
          break;
        case "connected":
        case "completed":
          this.transition("connected");
          break;
        case "disconnected":
          this.transition("reconnecting");
          break;
        case "failed":
          try {
            pc.restartIce();
            this.transition("reconnecting");
          } catch {
            this.tearDownPeer(socketId, "ice-failed");
          }
          break;
        default:
          break;
      }
    };
    this.peers.set(socketId, peer);
    this.emit("peerJoined", { socketId });
  }
  handlePeerLeft(socketId) {
    this.tearDownPeer(socketId, "client-left");
  }
  tearDownPeer(socketId, reason) {
    const peer = this.peers.get(socketId);
    if (!peer) {
      return;
    }
    try {
      peer.pc.close();
    } catch {
    }
    this.peers.delete(socketId);
    this.emit("peerLeft", { socketId });
    if (reason === "ice-failed") {
      this.emit("error", { code: "UNEXPECTED", message: `peer ${socketId} ICE failed` });
    }
    if (this.peers.size === 0) {
      this.transition("awaiting-peer");
    }
  }
  async onSdpMessage(raw) {
    const parsed = sdpMessageSchema2.safeParse(raw);
    if (!parsed.success) {
      return;
    }
    const message = parsed.data;
    const peer = this.peers.get(message.from);
    if (!peer) {
      return;
    }
    if (message.description.type === "offer") {
      try {
        await peer.pc.setRemoteDescription(message.description);
        const answer = await peer.pc.createAnswer();
        await peer.pc.setLocalDescription(answer);
        const reply = {
          to: message.from,
          from: this.mySocketId ?? "",
          description: { type: answer.type, sdp: answer.sdp ?? "" }
        };
        this.socket?.emit(SIGNALING_EVENTS2.sdp, reply);
      } catch (err) {
        this.emit("error", { code: "UNEXPECTED", message: "host SDP exchange failed", cause: err });
        this.tearDownPeer(message.from, "ice-failed");
      }
    }
  }
  async onIceMessage(raw) {
    const parsed = iceCandidateMessageSchema2.safeParse(raw);
    if (!parsed.success || !parsed.data.candidate) {
      return;
    }
    const peer = this.peers.get(parsed.data.from);
    if (!peer) {
      return;
    }
    try {
      await peer.pc.addIceCandidate(parsed.data.candidate);
    } catch (err) {
      this.emit("error", { code: "UNEXPECTED", message: "addIceCandidate failed", cause: err });
    }
  }
  forwardIceCandidate(toSocketId, candidate) {
    if (!this.socket || !this.mySocketId) {
      return;
    }
    const message = {
      to: toSocketId,
      from: this.mySocketId,
      candidate: candidate ? candidate.toJSON() : null
    };
    this.socket.emit(SIGNALING_EVENTS2.ice, message);
  }
};
export {
  DEFAULT_ADAPTIVE_BITRATE,
  DEFAULT_VIDEO_CODEC_PREFERENCE,
  LumeClient,
  LumeHost
};
//# sourceMappingURL=index.mjs.map